import { Ref, onMounted, watch, useRoute, useRouter } from '@nuxtjs/composition-api';

export type SortOption = {
  sortValue: string;
  dirValue: string;
  indexName: string;
};

export const useRouteSync = ({
  searchState,
  selectedSort,
  sortingOptions
}: {
  searchState: Ref<{
    query: string;
    page: number;
    indexName: string;
    // filters: string;
    facetFilters: string[][];
    numericFilters: string[];
  }>;
  selectedSort: Ref<SortOption>;
  sortingOptions: readonly SortOption[];
}) => {
  const route = useRoute();
  const router = useRouter();
  const defaultSort = sortingOptions[0];

  const pullFromRoute = () => {
    const {
      q,
      page,
      sort,
      dir,
      // filters: filtersParam,
      facetFilters: ffParam,
      numericFilters: nfParam
    } = route.value.query;

    // — query string
    if (typeof q === 'string') {
      searchState.value.query = q;
    }

    // — pagination
    if (typeof page === 'string') {
      const p = parseInt(page, 10);
      if (!isNaN(p) && p > 0) {
        searchState.value.page = p - 1;
      }
    }

    // — sort & direction
    if (typeof sort === 'string' && typeof dir === 'string') {
      const found = sortingOptions.find(
        s => s.sortValue === sort && s.dirValue === dir
      );
      if (found) {
        selectedSort.value = found;
        searchState.value.indexName = found.indexName;
      }
    }

    // — plain “filters” string (e.g. primaryTag.id:123)
    // if (typeof filtersParam === 'string') {
    //   searchState.value.filters = filtersParam;
    // }

    // — Algolia facetFilters (string[][]), JSON-encoded in URL
    if (typeof ffParam === 'string') {
      try {
        searchState.value.facetFilters = JSON.parse(ffParam);
      } catch (err) {
        console.warn('Invalid facetFilters in URL', err);
      }
    }

    // — Algolia numericFilters (string[]), JSON-encoded in URL
    if (typeof nfParam === 'string') {
      try {
        searchState.value.numericFilters = JSON.parse(nfParam);
      } catch (err) {
        console.warn('Invalid numericFilters in URL', err);
      }
    }
  };

  const buildNextQuery = (): Record<string, string> => {
    const next: Record<string, string> = {};

    // — query
    if (searchState.value.query) {
      next.q = searchState.value.query;
    }

    // — page
    if (searchState.value.page > 0) {
      next.page = String(searchState.value.page + 1);
    }

    // — sort
    if (
      selectedSort.value.sortValue !== defaultSort.sortValue ||
      selectedSort.value.dirValue !== defaultSort.dirValue
    ) {
      next.sort = selectedSort.value.sortValue;
      next.dir = selectedSort.value.dirValue;
    }

    // — plain filters
    // if (searchState.value.filters) {
    //   next.filters = searchState.value.filters;
    // }

    // — facetFilters
    const validFF = searchState.value.facetFilters.filter(group => Array.isArray(group) && group.length > 0);
    if (validFF.length > 0) {
      next.facetFilters = JSON.stringify(validFF);
    } else {
      delete next.facetFilters;
    }

    // — numericFilters
    if (searchState.value.numericFilters.length > 0) {
      next.numericFilters = JSON.stringify(searchState.value.numericFilters);
    } else {
      delete next.numericFilters;
    }

    return next;
  };

  const pushToRoute = () => {
    const nextQuery = buildNextQuery();

    // nothing changed?
    const currentQs = window.location.search.slice(1);
    const nextQs = new URLSearchParams(nextQuery).toString();
    if (currentQs === nextQs) {
      return;
    }

    if (process.client) {
      const qs = new URLSearchParams(nextQuery).toString();
      const base = window.location.pathname;
      window.history.replaceState(null, '', qs ? `${base}?${qs}` : base);
    } else {
      router.replace({ path: route.value.path, query: nextQuery }).catch(() => {});
    }
  };

  // seed state from URL on mount
  onMounted(pullFromRoute);

  // watch for changes and update URL
  watch(
    [
      () => searchState.value.query,
      () => searchState.value.page,
      () => selectedSort.value.sortValue,
      () => selectedSort.value.dirValue,
      // () => searchState.value.filters,
      () => searchState.value.facetFilters,
      () => searchState.value.numericFilters
    ],
    pushToRoute,
    { deep: true }
  );

  return { pullFromRoute, pushToRoute };
};
